class ThemeEffects {
  constructor() {
    this.revealObserver = null;
    this.scrollListenerInitialized = false;
    this.onScroll = this.onScroll.bind(this);
  }

  init(root = document) {
    this.onScroll();

    if (!this.scrollListenerInitialized) {
      document.addEventListener('scroll', this.onScroll, { passive: true });
      this.scrollListenerInitialized = true;
    }

    this.initReveal(root);
    this.initAnimatedDetails(root);
    this.initDragScroll(root);
    this.initScrollControls(root);
    this.initServiceHighlightsPagination(root);
    this.initLocalizationAutoSubmit(root);
    this.initHeaderLocalization(root);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  motionDuration(name, fallback) {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (!value) return fallback;
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return fallback;
    return value.endsWith('ms') ? parsed : parsed * 1000;
  }

  scrollBehavior() {
    return this.prefersReducedMotion() ? 'auto' : 'smooth';
  }

  onScroll() {
    document.documentElement.classList.toggle('site-scrolled', window.scrollY > 24);
  }

  initReveal(root = document) {
    const elements = root.querySelectorAll('[data-reveal]');

    if (!('IntersectionObserver' in window) || elements.length === 0) {
      elements.forEach((element) => element.setAttribute('data-revealed', ''));
      return;
    }

    if (!this.revealObserver) {
      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            entry.target.setAttribute('data-revealed', '');
            observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -10% 0px',
          threshold: 0.16,
        }
      );
    }

    elements.forEach((element) => this.revealObserver.observe(element));
  }

  initDragScroll(root = document) {
    const scrollers = root.querySelectorAll('[data-drag-scroll], [data-drag-scroll-mobile]');
    const mobileQuery = window.matchMedia('(max-width: 749px)');

    scrollers.forEach((scroller) => {
      if (scroller.dataset.dragScrollInitialized === 'true') {
        return;
      }

      scroller.dataset.dragScrollInitialized = 'true';

      let isDragging = false;
      let didDrag = false;
      let startX = 0;
      let scrollLeft = 0;
      let snapRestoreTimer = null;

      scroller.querySelectorAll('img, a').forEach((element) => {
        element.setAttribute('draggable', 'false');
      });

      scroller.addEventListener('dragstart', (event) => {
        event.preventDefault();
      });

      scroller.addEventListener('pointerdown', (event) => {
        if (scroller.hasAttribute('data-drag-scroll-mobile') && !mobileQuery.matches) {
          return;
        }

        if (event.button !== 0) {
          return;
        }

        if (event.pointerType === 'touch') {
          return;
        }

        isDragging = true;
        didDrag = false;
        startX = event.clientX;
        scrollLeft = scroller.scrollLeft;
        window.clearTimeout(snapRestoreTimer);
        scroller.classList.add('is-dragging');
        scroller.setPointerCapture(event.pointerId);
      });

      scroller.addEventListener('pointermove', (event) => {
        if (!isDragging) {
          return;
        }

        event.preventDefault();
        if (Math.abs(event.clientX - startX) > 4) {
          didDrag = true;
        }
        scroller.scrollLeft = scrollLeft - (event.clientX - startX);
      });

      scroller.addEventListener(
        'click',
        (event) => {
          if (!didDrag) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          didDrag = false;
        },
        true
      );

      const endDrag = (event) => {
        if (!isDragging) {
          return;
        }

        isDragging = false;
        scroller.classList.remove('is-dragging');
        scroller.classList.add('is-settling');
        snapRestoreTimer = window.setTimeout(() => {
          scroller.classList.remove('is-settling');
        }, this.motionDuration('--motion-duration-micro', 140));

        if (scroller.hasPointerCapture(event.pointerId)) {
          scroller.releasePointerCapture(event.pointerId);
        }
      };

      scroller.addEventListener('pointerup', endDrag);
      scroller.addEventListener('pointercancel', endDrag);
      scroller.addEventListener('pointerleave', endDrag);
    });
  }

  initScrollControls(root = document) {
    const sections = root.querySelectorAll('[data-scroll-track]');

    sections.forEach((track) => {
      if (track.dataset.scrollControlsInitialized === 'true') {
        return;
      }

      const container = track.closest('section');
      const previousButton = container?.querySelector('[data-scroll-prev]');
      const nextButton = container?.querySelector('[data-scroll-next]');

      if (!previousButton || !nextButton) {
        return;
      }

      track.dataset.scrollControlsInitialized = 'true';

      const scrollByCard = (direction) => {
        const firstCard = track.querySelector(':scope > *');
        const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : track.clientWidth * 0.8;
        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;

        track.scrollBy({
          left: direction * (cardWidth + gap),
          behavior: this.scrollBehavior(),
        });
      };

      previousButton.addEventListener('click', () => scrollByCard(-1));
      nextButton.addEventListener('click', () => scrollByCard(1));
    });
  }

  initServiceHighlightsPagination(root = document) {
    const sections = root.querySelectorAll('[data-service-highlights]');
    const mobileQuery = window.matchMedia('(max-width: 749px)');

    sections.forEach((section) => {
      if (section.dataset.serviceHighlightsPaginationInitialized === 'true') {
        return;
      }

      const track = section.querySelector('[data-service-highlights-track]');
      const items = Array.from(track?.querySelectorAll('.service-highlights__item') || []);
      const dots = Array.from(section.querySelectorAll('[data-service-highlight-dot]'));

      if (!track || items.length < 2 || dots.length < 2) {
        return;
      }

      section.dataset.serviceHighlightsPaginationInitialized = 'true';

      let frame = null;

      const setActiveDot = (activeIndex) => {
        dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
      };

      const getActiveIndex = () => {
        const trackCenter = track.scrollLeft + track.clientWidth / 2;

        return items.reduce(
          (closest, item, index) => {
            const itemCenter = item.offsetLeft + item.offsetWidth / 2;
            const distance = Math.abs(itemCenter - trackCenter);

            if (distance < closest.distance) {
              return { index, distance };
            }

            return closest;
          },
          { index: 0, distance: Number.POSITIVE_INFINITY }
        ).index;
      };

      const updateActiveDot = () => {
        frame = null;

        if (!mobileQuery.matches) {
          setActiveDot(0);
          return;
        }

        setActiveDot(getActiveIndex());
      };

      const requestUpdate = () => {
        if (frame) {
          return;
        }

        frame = window.requestAnimationFrame(updateActiveDot);
      };

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          const item = items[index];

          if (!item) {
            return;
          }

          const styles = window.getComputedStyle(track);
          const inlinePadding = Number.parseFloat(styles.paddingInlineStart || styles.paddingLeft || '0') || 0;
          const targetLeft = Math.max(0, item.offsetLeft - inlinePadding);

          track.scrollTo({
            left: targetLeft,
            behavior: this.scrollBehavior(),
          });
        });
      });

      track.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });

      requestUpdate();
    });
  }

  initLocalizationAutoSubmit(root = document) {
    const forms = root.querySelectorAll('.site-footer__localization-form, .site-header__localization-form, [data-localization-auto-submit]');

    forms.forEach((form) => {
      if (form.dataset.localizationAutoSubmitInitialized === 'true') {
        return;
      }

      form.dataset.localizationAutoSubmitInitialized = 'true';

      form.addEventListener('change', (event) => {
        const selector = event.target.closest('select[name="country_code"], select[name="locale_code"]');

        if (!selector || !form.contains(selector)) {
          return;
        }

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
          return;
        }

        form.submit();
      });
    });
  }

  initHeaderLocalization(root = document) {
    const shortcuts = root.querySelectorAll('[data-header-localization]');

    shortcuts.forEach((shortcut) => {
      if (shortcut.dataset.headerLocalizationInitialized === 'true') {
        return;
      }

      shortcut.dataset.headerLocalizationInitialized = 'true';

      shortcut.addEventListener('toggle', () => {
        if (!shortcut.open) {
          return;
        }

        document.querySelectorAll('[data-header-localization]').forEach((otherShortcut) => {
          if (otherShortcut !== shortcut) {
            otherShortcut.open = false;
          }
        });
      });

      document.addEventListener('click', (event) => {
        if (!shortcut.open || shortcut.contains(event.target)) {
          return;
        }

        shortcut.open = false;
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !shortcut.open) {
          return;
        }

        shortcut.open = false;
        shortcut.querySelector('summary')?.focus();
      });
    });
  }

  initAnimatedDetails(root = document) {
    root.querySelectorAll('details[data-animated-details]').forEach((details) => {
      if (details.dataset.animatedDetailsReady === 'true') return;

      const summary = details.querySelector('summary');
      const content = details.querySelector('[data-animated-details-content]');
      if (!summary || !content) return;

      details.dataset.animatedDetailsReady = 'true';

      summary.addEventListener('click', (event) => {
        if (this.prefersReducedMotion()) return;

        event.preventDefault();
        if (details.dataset.animating === 'true') return;

        if (details.open) {
          this.closeDetails(details, content);
        } else {
          this.openDetails(details, content);
        }
      });
    });
  }

  openDetails(details, content) {
    details.dataset.animating = 'true';
    details.open = true;
    content.style.height = '0px';
    content.style.opacity = '0';

    requestAnimationFrame(() => {
      content.style.height = `${content.scrollHeight}px`;
      content.style.opacity = '1';
    });

    this.onHeightTransitionEnd(content, () => {
      content.style.height = '';
      content.style.opacity = '';
      details.dataset.animating = 'false';
    });
  }

  closeDetails(details, content) {
    details.dataset.animating = 'true';
    content.style.height = `${content.scrollHeight}px`;
    content.style.opacity = '1';

    requestAnimationFrame(() => {
      content.style.height = '0px';
      content.style.opacity = '0';
    });

    this.onHeightTransitionEnd(content, () => {
      details.open = false;
      content.style.height = '';
      content.style.opacity = '';
      details.dataset.animating = 'false';
    });
  }

  onHeightTransitionEnd(element, callback) {
    const onEnd = (event) => {
      if (event.target !== element || event.propertyName !== 'height') return;
      element.removeEventListener('transitionend', onEnd);
      callback();
    };

    element.addEventListener('transitionend', onEnd);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const themeEffects = new ThemeEffects();

  themeEffects.init();

  document.addEventListener('shopify:section:load', (event) => {
    themeEffects.init(event.target);
  });
});
